import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { CourseLimitError } from '../lib/tier-errors';

type QuestionOptions = string[] | string | null;
type QuestionAnswer = string | number | number[];

export interface Question {
  id: number;
  content: string;
  type: string;
  options: QuestionOptions;
  correct_answer: QuestionAnswer;
  explanation: string;
  difficulty: string;
  level_id: number;
  course_id: number;
}

export interface Level {
  id: number;
  level: number;
  name: string;
  description: string;
  difficulty?: string;
  topics?: string[];
  questions: Question[];
}

interface CourseDepartment {
  id: number;
  name: string;
}

interface CourseDetails {
  id: number;
  name: string;
  status: string;
  randomize_questions: boolean;
  departments: CourseDepartment[];
  levels: Level[];
}

interface ListCourse {
  id: number;
  name: string;
  status: string;
  course_departments: { departments: CourseDepartment }[];
  created_at: string;
}

interface PaginatedCourses {
  courses: ListCourse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface GetCourseResponse {
  course: CourseDetails;
}

interface CreateCourseData {
  name: string;
  description: string;
  departmentIds: number[];
  organisationId: number;
  purpose: string;
  subjectMatter: string;
  creationMethod: string;
  content?: { content: string; fileName: string }[];
}

interface CreateCourseResponse {
  course: {
    id: number;
    name: string;
  };
}

async function fetchCourses(
  organisationId: number,
  page: number,
  limit: number,
  status?: string,
  search?: string,
): Promise<PaginatedCourses> {
  const response = await apiFetch(
    ENDPOINTS.COURSE_LIST(organisationId, page, limit, status, search),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch courses');
  }

  return response.json();
}

async function fetchCourse(
  courseId: number,
  organisationId: number,
): Promise<GetCourseResponse> {
  const response = await apiFetch(
    ENDPOINTS.COURSE_GET(courseId, organisationId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch course');
  }

  return response.json();
}

export function useCourses(
  organisationId: number | undefined,
  page: number,
  limit: number,
  status?: string,
  search?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['course', 'list', organisationId, page, limit, status, search],
    queryFn: () => fetchCourses(organisationId!, page, limit, status, search),
    enabled: !!organisationId && enabled,
  });
}

export function useCourse(
  courseId: number,
  organisationId: number | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['course', courseId, organisationId],
    queryFn: () => fetchCourse(courseId, organisationId!),
    enabled: enabled && !!organisationId,
    retry: false,
  });
}

interface ToggleStatusResponse {
  status: string;
}

async function toggleCourseStatus(params: {
  courseId: number;
  organisationId: number;
}): Promise<ToggleStatusResponse> {
  const response = await apiFetch(
    ENDPOINTS.COURSE_TOGGLE_STATUS(params.courseId, params.organisationId),
    { method: 'PATCH' },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.code === CourseLimitError.CODE) {
      throw new CourseLimitError(errorData.message);
    }
    throw new Error(errorData.message || 'Failed to toggle course status');
  }

  return response.json();
}

export function useToggleCourseStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleCourseStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course'] });
    },
  });
}

async function toggleCourseRandomize(params: {
  courseId: number;
  organisationId: number;
}): Promise<{ randomize_questions: boolean }> {
  const response = await apiFetch(
    ENDPOINTS.COURSE_TOGGLE_RANDOMIZE(params.courseId, params.organisationId),
    { method: 'PATCH' },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to toggle randomize setting');
  }

  return response.json();
}

export function useToggleCourseRandomize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleCourseRandomize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course'] });
    },
  });
}

async function createCourse(
  data: CreateCourseData,
): Promise<CreateCourseResponse> {
  const response = await apiFetch(ENDPOINTS.COURSE_CREATE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create course');
  }

  return response.json();
}

export function useCreateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course'] });
    },
  });
}

interface DeleteCourseResponse {
  message: string;
}

async function deleteCourse(params: {
  courseId: number;
  organisationId: number;
}): Promise<DeleteCourseResponse> {
  const response = await apiFetch(
    ENDPOINTS.COURSE_DELETE(params.courseId, params.organisationId),
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete course');
  }

  return response.json();
}

export function useDeleteCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course'] });
    },
  });
}

async function updateCourse(params: {
  courseId: number;
  organisationId: number;
  name?: string;
  departmentIds?: number[];
}): Promise<{ name?: string; departments: CourseDepartment[] }> {
  const body: Record<string, unknown> = {};
  if (params.name !== undefined) body.name = params.name;
  if (params.departmentIds !== undefined) {
    body.departmentIds = params.departmentIds;
  }

  const response = await apiFetch(
    ENDPOINTS.COURSE_UPDATE(params.courseId, params.organisationId),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update course');
  }

  return response.json();
}

export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course'] });
    },
  });
}

interface DuplicateCourseResponse {
  id: number;
}

async function duplicateCourse(params: {
  courseId: number;
  organisationId: number;
}): Promise<DuplicateCourseResponse> {
  const response = await apiFetch(
    ENDPOINTS.COURSE_DUPLICATE(params.courseId, params.organisationId),
    { method: 'POST' },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to duplicate course');
  }

  return response.json();
}

export function useDuplicateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: duplicateCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course'] });
    },
  });
}
